import { memo, useMemo, useEffect, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import queryString from 'query-string';
import { v4 } from 'uuid';
import PropTypes from 'prop-types';
import { useModal, KitIcon, AlloyButton } from 'cat-ecommerce-alloy';
import { useFormikContext } from 'formik';
import AddSavedListModal from '@app/components/saved-lists/AddSavedListModal';
import CreateSavedListModal from '@app/components/saved-lists/CreateSavedListModal';
import ExceptionFeedback from '@app/components/common/Exception/ExceptionFeedback';
import useSavedLists from '@app/components/saved-lists/useSavedLists';
import { CSR_TEXTS } from '@app/components/pages/csr-landing/texts';
import ShoppingCartLoader from '../ShoppingCartLoader';
import CartItemsTable from './OrderItemsTable/CartItemsTable';
import PackageQuantityTable from './OrderItemsTable/PackageQuantityTable';
import OrderItemsHeader from './OrderItemsHeader';
import OrderSubtotal from './common/OrderSubtotal';
import EmptyCartButton from './common/EmptyCartButton';
import ProceedToCheckoutButton from './common/ProceedToCheckoutButton';
import EmailToCartButton from './common/EmailToCartButton';
import RefreshContactButtons from './common/RefreshContactButtons/RefreshContactButtons';
import LineAttributeItem from './common/LineItemAttributes/LineAttributeItem';
import BundleError from './common/BundleError';
import useAnalytics from '@app/hooks/useAnalytics/useAnalytics';
import useOrderUpdateToasts from './hooks/useOrderUpdateToasts';
import useQuickOrderGA from '../../hooks/useQuickOrderGA';
import {
  getCookie,
  isEmpty,
  removeCookie,
  replaceTokensInString
} from '@app/utils';
import { getGACheckoutProducts } from '@app/hooks/useAnalytics/utils';
import {
  convertFormattedNumberToUnformatted,
  getCartItemsCount
} from '../../utils';
import { getItemsFromSisFallbackCookie } from '../QuickOrder/utils';
import { normalizeError } from '@app/store/exception/utils';
import { ADD_TO_CART_ITEMS } from '@app/constants/cookieContants';
import {
  STATUS,
  USER_TYPE_GUEST,
  AFFILIATION_DEALER,
  GENERIC_USER_ID
} from '@app/constants/commonConstants';
import {
  ERROR_DOMAIN,
  ERROR_PATH,
  FEEDBACK_TYPES
} from '@app/constants/errorConstants';
import { DISABLE_ORDERS_PROCEED_TO_CHECKOUT_BUTTON_EVENT } from '@app/constants/events';
import {
  HIDDEN_INPUT_NUM_ORDER_ITEMS,
  QUICK_ORDER_SECTION_HEADER_TARGET,
  SIS_TO_CART_FALLBACK
} from './constants';
import { SIS_TO_CART_FALLBACK_FEATURE_FLAG } from '@app/constants/featureFlags';
import { LIST_PCC_CART_PAGE } from '@app/constants/analyticsConstants';
import { DEFAULT_ROW_VALUES } from '../ShoppingCartPage/constants';
import { loadSavedListDetails } from '@app/store/account/actions';
import { disableProceedToCheckoutButton } from '@app/store/orders/actions';
import { setError } from '@app/store/exception/actions';
import styles from './OrderItems.module.scss';

const OrderItems = memo(
  ({
    modalOpen,
    displayAvailabityAndSourcingDetails,
    setTogglePrefs,
    showNotes
  }) => {
    const [t] = useTranslation();
    const dispatch = useDispatch();
    const {
      setShowSavedListsModalAfterADP,
      setIsPendingSavedListsModal,
      saveListsResolved,
      isPendingSavedListsModal,
      itemToSave
    } = useSavedLists();
    const addSavedListModalProps = useModal();
    const createSavedListModalProps = useModal();
    const { fireProductImpressionEvent, fireViewCartEvent } = useAnalytics();
    const { fireQOAddToCartGAEvent } = useQuickOrderGA();
    const location = useLocation();
    const history = useHistory();
    const { values, setFieldValue } = useFormikContext();
    const [isImportWarningDisplay, setIsImportWarningDisplay] = useState(true);
    const [areQOItemsUpdated, setAreQOItemsUpdated] = useState(false);

    const ascendingOrderItems = useSelector(
      s => s.orders.byId[s.orders.current]?.ascendingOrderItems || []
    );
    const orderTotals =
      useSelector(s => s.orders.byId[s.orders.current]?.orderTotals) ?? {};
    const orderTotal =
      orderTotals?.unformattedOrderSubTotal ||
      convertFormattedNumberToUnformatted(orderTotals?.orderSubTotal || '');
    const dealerStoreCommon = useSelector(s => s.common.storeLocationName);
    const dealerStore =
      orderTotals?.orderExtendAttribute?.dealerStore || dealerStoreCommon;

    const showSisWarningMessage = useSelector(
      s => s.featureFlag?.[SIS_TO_CART_FALLBACK_FEATURE_FLAG]
    );
    const {
      hasCartError,
      hasOrderData,
      hasOrderWithPrice,
      getCurrentOrderSuccess,
      checkoutType,
      getCurrentOrderStatus
    } = useSelector(s => s.orders);
    const savedListsStatus = useSelector(s => s.account.savedListsStatus);
    const {
      userType,
      emailCart,
      isCSPCustomer,
      userAffiliation,
      userId,
      isBuyOnBehalf = false
    } = useSelector(s => s.common);

    const shoppingPreferenceDataStatus = useSelector(
      s => s.shoppingPreferences.shoppingPreferenceDataStatus
    );
    const shoppingPreferencesLoaded =
      shoppingPreferenceDataStatus === STATUS.REJECTED ||
      shoppingPreferenceDataStatus === STATUS.RESOLVED;
    const displayQuickOrder = useSelector(
      s => s.shoppingPreferences.shoppingPreferencesData.displayQuickOrder
    );
    const isDealerUser = userAffiliation === AFFILIATION_DEALER;
    const showQuickOrder = displayQuickOrder || isDealerUser;
    const shoppingCartError = useSelector(
      s => s.errors[ERROR_DOMAIN.CART]?.[ERROR_PATH.SHOPPING_CART]
    );
    const isGuest = userType === USER_TYPE_GUEST;

    const savedLists = useSelector(s => s.account?.savedLists);
    const hasSavedLists = savedLists?.length > 0;

    const { fromQuickOrderLink, ...rest } = useMemo(
      () => queryString.parse(location.search),
      [location]
    );
    const updatedRouteWithQueryParams = useMemo(
      () => queryString.stringify(rest),
      [rest]
    );
    const hasOrderWithPriceOrCartEmpty =
      hasOrderWithPrice ||
      (getCurrentOrderSuccess && emailCart) ||
      hasOrderData;

    const isGenericUser = userId === GENERIC_USER_ID;

    const orderInformationStatus = useSelector(
      s => s.checkout.setOrderInformationStatus
    );
    const orderInformationResolved = orderInformationStatus === STATUS.RESOLVED;

    const isPartsRemovedByInvalidSOS = useSelector(
      s => s.checkout.isPartsRemovedByInvalidSOS
    );
    const removedParts = useSelector(s => s.checkout.removedParts);
    const isWarningDisplay = useSelector(
      s => s.errors[ERROR_DOMAIN.CART]?.[ERROR_PATH.SIS_TO_CART_IMPORT]
    );
    const updateQuickOrderItemsAndSetSosWarning = useCallback(
      (newItems = []) => {
        const oldItems = values?.items?.filter(i =>
          Object.keys(i).some(key => key !== 'id' && !!i[key])
        );
        setFieldValue('items', [
          ...newItems,
          ...oldItems,
          {
            ...DEFAULT_ROW_VALUES,
            id: v4()
          }
        ]);
        const removedPartsNumbers = newItems.map(p => p.itemNumber).join(', ');
        const message = replaceTokensInString(
          t('ERR_ITEM_SOS'),
          removedPartsNumbers
        );
        dispatch(
          setError(ERROR_DOMAIN.CART, ERROR_PATH.SHOPPING_CART, {
            ...normalizeError({}, { message, severity: 'warning' }),
            skipTranslation: true
          })
        );
      },
      [dispatch, setFieldValue, t, values?.items]
    );

    const dispatchProceedToCheckout = useCallback(() => {
      dispatch(disableProceedToCheckoutButton());
    }, [dispatch]);

    useEffect(() => {
      document.addEventListener(
        DISABLE_ORDERS_PROCEED_TO_CHECKOUT_BUTTON_EVENT,
        dispatchProceedToCheckout
      );
      return () =>
        document.removeEventListener(
          DISABLE_ORDERS_PROCEED_TO_CHECKOUT_BUTTON_EVENT,
          dispatchProceedToCheckout
        );
    }, [dispatch, dispatchProceedToCheckout]);
    useEffect(() => {
      removeCookie(SIS_TO_CART_FALLBACK);
    }, []);
    useEffect(() => {
      if (!isGuest && savedListsStatus === STATUS.IDLE) {
        dispatch(loadSavedListDetails());
      }
    }, [isGuest, dispatch, savedListsStatus]);

    const setRoute = useCallback(() => {
      history.replace({
        pathname: location.pathname,
        search: updatedRouteWithQueryParams
      });
    }, [history, location, updatedRouteWithQueryParams]);

    useEffect(() => {
      if (hasOrderWithPrice || (getCurrentOrderSuccess && emailCart)) {
        const gaProducts = getGACheckoutProducts({
          products: ascendingOrderItems,
          emailCart
        });

        let checkoutType = '';
        if (userType === 'R' || userType === 'A') {
          if (isCSPCustomer) {
            checkoutType = 'Logged In B2B';
          } else {
            checkoutType = 'Logged In IA';
          }
        } else {
          checkoutType = 'Guest';
        }
        fireQOAddToCartGAEvent(gaProducts, ADD_TO_CART_ITEMS);
        fireProductImpressionEvent(LIST_PCC_CART_PAGE, gaProducts);
        fireViewCartEvent({
          products: gaProducts,
          orderTotal,
          dealerStore
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      emailCart,
      hasOrderWithPrice,
      fireQOAddToCartGAEvent,
      fireProductImpressionEvent,
      fireViewCartEvent,
      getCurrentOrderSuccess
    ]);

    useEffect(() => {
      if (hasOrderWithPriceOrCartEmpty) {
        if (
          fromQuickOrderLink &&
          document.getElementById(QUICK_ORDER_SECTION_HEADER_TARGET)
        ) {
          document
            .getElementById(QUICK_ORDER_SECTION_HEADER_TARGET)
            .scrollIntoView();
          setRoute();
        }
      }
    }, [fromQuickOrderLink, setRoute, hasOrderWithPriceOrCartEmpty]);

    useEffect(() => {
      if (
        saveListsResolved &&
        isPendingSavedListsModal &&
        hasOrderData &&
        !isGuest
      ) {
        setShowSavedListsModalAfterADP(false);
        setIsPendingSavedListsModal(false);
        if (hasSavedLists) {
          addSavedListModalProps.open();
        } else {
          createSavedListModalProps.open();
        }
      }
    }, [isGuest, saveListsResolved, hasOrderData, isPendingSavedListsModal]);

    useEffect(() => {
      if (
        orderInformationResolved &&
        isPartsRemovedByInvalidSOS &&
        removedParts?.length
      ) {
        const newItems = removedParts.map(p => ({
          itemNumber: p.partNumber,
          quantity: p.quantity?.toString()
        }));
        updateQuickOrderItemsAndSetSosWarning(newItems);
      }
    }, [
      updateQuickOrderItemsAndSetSosWarning,
      orderInformationResolved,
      isPartsRemovedByInvalidSOS,
      removedParts
    ]);

    useOrderUpdateToasts();

    const showPackageQuantityTable = ascendingOrderItems.some(
      item => !!item.packageError
    );
    const showCartTable = ascendingOrderItems.some(item => !item.packageError);

    const itemHasServerErrors = useMemo(
      () => ascendingOrderItems.some(item => item.errors.server),
      [ascendingOrderItems]
    );

    const itemHasServiceOptionInstruction = useMemo(
      () =>
        ascendingOrderItems.some(
          item =>
            item.orderItemExtendAttribute?.selfServiceOptionInstructionLink
        ),
      [ascendingOrderItems]
    );

    const getCartButton = className => {
      return emailCart || checkoutType === 'emailCart' ? (
        <EmailToCartButton className={`${className} d-print-none`} />
      ) : (
        <ProceedToCheckoutButton className={`${className} d-print-none`} />
      );
    };

    // TODO: Change breakpoints to md when table schema changes to accomodate
    // new column display classes.
    const getComponentErrors = () => {
      if (!isEmpty(shoppingCartError)) {
        let errorMessage = '';
        if (Array.isArray(shoppingCartError.message)) {
          errorMessage =
            shoppingCartError.message?.length > 1 ? (
              <ul>
                {shoppingCartError.message.map((msg, index) => (
                  <li key={index}> {msg} </li>
                ))}
              </ul>
            ) : (
              <p>{shoppingCartError.message[0]}</p>
            );
        } else {
          errorMessage = <p>{shoppingCartError.message}</p>;
        }

        return (
          <div className="py-2">
            <ExceptionFeedback
              title={shoppingCartError.title}
              type={shoppingCartError.severity}
            >
              {errorMessage}
            </ExceptionFeedback>
          </div>
        );
      } else {
        if (itemHasServerErrors) {
          return (
            <div className="pt-2 pt-lg-0">
              <ExceptionFeedback>
                <div
                  className={cx(styles['order-items__system-feedback'], {
                    'pb-2': isBuyOnBehalf
                  })}
                >
                  {isBuyOnBehalf
                    ? CSR_TEXTS.CSR_REFRESH_ERROR_MESSAGE
                    : t('USER_REFRESH_ERROR_MESSAGE')}
                </div>
                {!isBuyOnBehalf && <RefreshContactButtons />}
              </ExceptionFeedback>
            </div>
          );
        }
        return <BundleError orderItems={ascendingOrderItems} />;
      }
    };
    const noOfItemsInCart = getCartItemsCount(ascendingOrderItems);
    const hasItemsInCart = ascendingOrderItems.length > 0;

    const clearImportError = () => {
      setIsImportWarningDisplay(false);
      removeCookie(SIS_TO_CART_FALLBACK);
    };

    const sisFallbackCookie = getCookie(SIS_TO_CART_FALLBACK);

    const importToQuickOrder = () => {
      modalOpen(true);
      const itemsInCookie =
        sisFallbackCookie && getItemsFromSisFallbackCookie(userId);
      if (!areQOItemsUpdated && itemsInCookie) {
        setFieldValue('items', itemsInCookie);
      }
      setAreQOItemsUpdated(true);
      removeCookie(SIS_TO_CART_FALLBACK);
      setIsImportWarningDisplay(false);
    };
    const getSisImportErrors = () => {
      if (
        !isEmpty(isWarningDisplay) &&
        isImportWarningDisplay &&
        !shoppingCartError &&
        showSisWarningMessage
      ) {
        return (
          <div className="py-2">
            <ExceptionFeedback
              title={isWarningDisplay.title}
              type={FEEDBACK_TYPES.WARNING}
            >
              <p>{isWarningDisplay.message}</p>
              <div>
                <AlloyButton
                  buttonType="secondary"
                  size="small"
                  className={`${styles['import-quick-order-btn']} u-fs--16 u-fs-lg--14 `}
                  onClick={importToQuickOrder}
                >
                  {t('IMPORT_QUICK_ORDER')}
                </AlloyButton>
                <AlloyButton
                  buttonType="ghost"
                  size="small"
                  className={`${styles['dismiss-button']} u-fs--16 u-fs-lg--14 d-block float-md-end mt-2 mt-md-0 `}
                  onClick={clearImportError}
                >
                  {t('DISMISS')}
                </AlloyButton>
              </div>
            </ExceptionFeedback>
          </div>
        );
      }
    };
    return (
      <div
        className={cx(
          styles['orderitems-unset-globals'],
          styles['orderitems__container'],
          { [styles['orderitems__container-large-spacing']]: showQuickOrder },
          { [styles['orderitems__container-small-spacing']]: !showQuickOrder },
          { 'mb-5': !hasItemsInCart && !isGuest }
        )}
        data-testid="order-items-container"
      >
        <OrderItemsHeader
          hasCartError={hasCartError}
          hasItemsInCart={hasItemsInCart}
          noOfItemsInCart={noOfItemsInCart}
        />
        {getSisImportErrors()}
        {(!isGenericUser && getCurrentOrderStatus === STATUS.IDLE) ||
        getCurrentOrderStatus === STATUS.PENDING ||
        (!shoppingPreferencesLoaded && !isGuest) ? (
          <ShoppingCartLoader />
        ) : (
          <>
            {/**for print view only */}
            <div className="d-print-block d-none">
              <div className="row align-items-center">
                <div
                  className={`${styles['expander-button-label']} col-9 text-uppercase u-color--Black`}
                >
                  {t('SHOPCART_ITEMS_IN_CART')}
                </div>
                <div className="col-3 text-end">
                  <span className={`${styles['number-items-print']}`}>
                    {replaceTokensInString(
                      t('MSC_ITEMS'),
                      ascendingOrderItems.length
                    )}
                  </span>
                </div>
              </div>
            </div>
            {itemHasServiceOptionInstruction && (
              <div className="px-1 pb-3 pb-lg-3 d-print-none">
                <LineAttributeItem
                  icon={KitIcon}
                  title={t('SSRO_INSTRUCTION_TEXT')}
                />
              </div>
            )}
            {hasItemsInCart && (
              <>
                {getComponentErrors()}
                {showPackageQuantityTable && <PackageQuantityTable />}
                {showCartTable && (
                  <CartItemsTable
                    displayAvailabityAndSourcingDetails={
                      displayAvailabityAndSourcingDetails
                    }
                    setTogglePrefs={setTogglePrefs}
                    showNotes={showNotes}
                  />
                )}
                <input
                  type="hidden"
                  name={HIDDEN_INPUT_NUM_ORDER_ITEMS}
                  id={HIDDEN_INPUT_NUM_ORDER_ITEMS}
                  value={ascendingOrderItems.length}
                />
              </>
            )}
            {!hasItemsInCart && !hasCartError && (
              <>
                {getComponentErrors()}
                <p
                  className={cx('u-fs--12 u-color--MediumGray', {
                    'mb-5': !isGuest,
                    'mb-4': isGuest
                  })}
                >
                  {t('SHOPCART_EMPTY_CART')}
                </p>
              </>
            )}
            <div className="row">
              {hasItemsInCart && (
                <div
                  className={`col-12 w-100 ${styles['order-table__footer']}`}
                >
                  <div className="row d-flex">
                    <div className="col-md-6 col-12 order-md-2 pt-0">
                      <OrderSubtotal />
                      {
                        <div className="d-flex justify-content-end py-2 py-md-3">
                          {getCartButton()}
                        </div>
                      }
                    </div>
                    <div className="col-md-6 col-12 order-1">
                      <EmptyCartButton />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
        <AddSavedListModal
          {...addSavedListModalProps}
          addSavedListData={itemToSave}
        />
        <CreateSavedListModal
          {...createSavedListModalProps}
          createSavedListData={itemToSave}
        />
      </div>
    );
  }
);

OrderItems.displayName = 'OrderItems';

OrderItems.propTypes = {
  displayAvailabityAndSourcingDetails: PropTypes.bool,
  setTogglePrefs: PropTypes.func,
  showNotes: PropTypes.bool,
  modalOpen: PropTypes.bool
};

export default OrderItems;
